import type {
  ApiResponse,
  FeeEstimate,
  FeeWarning,
  GasConditions,
  Transfer,
  TransferRequest,
  TransferResponse,
  Chain,
  ReferrerRegistration,
  ReferrerStats,
  ReferrerEarnings,
} from '@/types'
import { BRIDGE_CHAINS, DEX_CHAINS } from '@/config/chains'
import { isTronSamechainOneclickPreviewEnabled } from '@/config/flags'
import { ethers } from 'ethers'
import { getDecimals } from '@/utils/decimals'
// Re-export from standalone file to preserve existing import paths
import type { SupportedToken } from '@/config/fallback-tokens'
export { FALLBACK_TOKENS, type SupportedToken } from '@/config/fallback-tokens'

const API_BASE = '/api'

const API_KEY = import.meta.env.VITE_PUBLIC_API_KEY as string | undefined;

/** Shared API key header for all frontend fetch calls */
export const apiKeyHeader: Record<string, string> = API_KEY ? { 'X-API-Key': API_KEY } : {};

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...apiKeyHeader,
        ...options?.headers,
      },
      ...options,
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any
    try {
      data = await response.json()
    } catch {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Server unavailable. Please try again later.',
        },
      }
    }

    if (!response.ok) {
      // Handle both nested { error: { code, message } } and flat { error: 'CODE', message: '...' } formats
      const errorCode = typeof data.error === 'string'
        ? data.error
        : (data.error?.code || 'UNKNOWN_ERROR')
      // A flat string `error` (e.g. DEX /bridge: error: "Transaction simulation failed") IS the
      // human message — use it, don't fall through to the generic text. Append a contract revertReason
      // when present so the user sees WHY, not just "an unknown error occurred".
      const baseMessage = data.message
        || (typeof data.error === 'string' ? data.error : data.error?.message)
        || 'An unknown error occurred'
      const errorMessage = typeof data.revertReason === 'string' && data.revertReason
        ? `${baseMessage}: ${data.revertReason}`
        : baseMessage
      return {
        success: false,
        error: {
          code: errorCode,
          message: errorMessage,
        },
        // HTTP status of the failed response. Lets a caller act on the SPECIFIC failure — e.g. the
        // order-status poller stops forever-polling a 404 (order unknown to the backend) while still
        // retrying transient 5xx/network errors. Present only on non-OK responses.
        status: response.status,
        // Passthrough: some endpoints attach a numeric `minAmountUsd` to a 400 body (DEX /estimate when the
        // input is below the 1Click route floor). Preserve it so a specialized caller can surface the floor
        // and gate on it; undefined for every other endpoint. Cast because ApiResponse<T> models no per-
        // endpoint extras — the field is read only where the caller widens the return type to expose it.
        ...(typeof data.minAmountUsd === 'number' ? { minAmountUsd: data.minAmountUsd } : {}),
      } as ApiResponse<T>
    }

    return data
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Server unavailable. Please try again later.',
      },
    }
  }
}

/**
 * Bridge API endpoints
 */
export const bridgeApi = {
  /**
   * Get fee estimate for a transfer
   * Backend endpoint: GET /api/quote/estimate?sourceChain=...&destChain=...&amount=...
   */
  async estimateFee(
    amount: string,
    sourceChain: Chain,
    destinationChain: Chain,
    userAddress?: string
  ): Promise<ApiResponse<FeeEstimate>> {
    // Convert display amount to atomic units using source chain's decimal precision
    const srcDecimals = getDecimals(sourceChain, 'USDC')
    const amountUsdc = ethers.parseUnits(amount, srcDecimals).toString()

    const params = new URLSearchParams({
      sourceChain,
      destChain: destinationChain,
      amount: amountUsdc,
    })
    if (userAddress) {
      params.set('userAddress', userAddress)
    }

    const response = await fetchApi<{
      estimate: {
        grossAmount: string
        percentageFeeBps: number
        percentageFee: string
        networkFeeUsdc: string
        circleMaxFeeUsdc?: string
        totalFeeUsdc: string
        netAmount: string
        networkFeeUsd: number
        networkFeeNativeDisplay: string
        sourceChain: string
        destChain: string
        sourceDecimals: number
        destDecimals: number
        minAmountUsd?: number
        minSource?: string
        minSourceUsd?: number
        belowMin?: boolean
      }
      referrer?: { address: string; shareBps: number } | null
      feeWarning: FeeWarning | null
      gasConditions?: GasConditions
    }>(`/quote/estimate?${params}`)

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || { code: 'UNKNOWN_ERROR', message: 'Failed to estimate fees' },
      }
    }

    const { estimate, referrer, feeWarning, gasConditions } = response.data
    // Use sourceDecimals from response for display conversion
    const sd = estimate.sourceDecimals ?? srcDecimals
    const percentageFeeDisplay = ethers.formatUnits(estimate.percentageFee, sd)
    const networkFeeUsdcDisplay = ethers.formatUnits(estimate.networkFeeUsdc, sd)
    const netAmountDisplay = ethers.formatUnits(estimate.netAmount, sd)
    // Circle's fast-transfer pass-through — its own line so the breakdown sums to PAY − RECEIVE
    // (netAmount already subtracts it). Absent on Solana-source (the program supplies its own).
    const circleMaxFeeDisplay = estimate.circleMaxFeeUsdc
      ? ethers.formatUnits(estimate.circleMaxFeeUsdc, sd)
      : undefined

    // Compute referrer fee amount frontend-side from percentageFee * shareBps / 10000
    const mappedReferrer = referrer ? {
      address: referrer.address,
      feeAmount: ethers.formatUnits(
        BigInt(estimate.percentageFee) * BigInt(referrer.shareBps) / 10000n,
        sd
      ),
      shareBps: referrer.shareBps,
    } : null

    return {
      success: true,
      data: {
        baseFee: percentageFeeDisplay,
        totalFee: percentageFeeDisplay, // platform fees only (percentage fee)
        networkFeeUsdc: networkFeeUsdcDisplay,
        networkFeeNativeDisplay: estimate.networkFeeNativeDisplay,
        estimatedReceived: netAmountDisplay, // net after ALL fees (platform + network + Circle)
        minimumReceived: netAmountDisplay, // Same as estimated for CCTP (no slippage)
        estimatedTime: 60, // seconds (always fast mode)
        feeWarning: feeWarning ?? null,
        referrer: mappedReferrer,
        gasConditions: gasConditions ?? null,
        circleMaxFeeUsdc: circleMaxFeeDisplay,
        minReceiveUsd: estimate.minAmountUsd,
        minSourceUsd: estimate.minSourceUsd,
        belowMin: estimate.belowMin,
      },
    }
  },

  /**
   * Initiate a hybrid bridge transfer (user burns, backend mints)
   */
  async initiateHybridTransfer(
    request: TransferRequest
  ): Promise<ApiResponse<TransferResponse>> {
    const response = await fetchApi<TransferResponse>('/bridge/hybrid', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    // ── G2: a 409 from THIS endpoint is a SUCCESS, not a failure ─────────────────────────────────
    // `/hybrid` answers 409 "Transfer already registered" when a row already exists for this burn
    // hash. That is the desired end state — the transfer IS registered and the relayer will mint —
    // but the body carries `success: false`, and the generic `if (!response.ok)` in `fetchApi`
    // converts it into an error. The retry wrapper then collapses 400 (fatal), 409 (success) and
    // 451 (compliance) into one identical "Failed to submit to backend" message, so a user whose
    // transfer went through was told it had not.
    //
    // The rule is deliberately scoped to this endpoint rather than applied inside `fetchApi`: the
    // other 409s in the API (referrer code taken, terms version conflict) are genuine conflicts
    // where success would be a lie. 409-means-success is a property of /hybrid, not of HTTP.
    //
    // `transferId` is `burnTxHash` by construction on both the 200 and the 409 (routes/hybrid.ts),
    // so reconstructing it from the request is exact, not a guess.
    if (!response.success && response.status === 409) {
      return {
        success: true,
        data: {
          transferId: request.burnTxHash,
          burnTxHash: request.burnTxHash,
          status: 'burn_success',
          estimatedTime: 0,
        } as TransferResponse,
      }
    }

    return response
  },

  /**
   * Get transfer status by burn transaction hash
   */
  async getTransferStatus(burnTxHash: string): Promise<ApiResponse<Transfer>> {
    return fetchApi(`/bridge/hybrid/status/${burnTxHash}`)
  },

  /**
   * Get pending transfers for a specific wallet address
   */
  async getPendingTransfers(address: string): Promise<ApiResponse<{ pending: Transfer[] }>> {
    return fetchApi(`/relayer/pending?address=${encodeURIComponent(address)}`)
  },

  /**
   * Get transfer history for an address
   */
  async getTransferHistory(address: string | string[]): Promise<ApiResponse<Transfer[]>> {
    // Pass every connected wallet (comma-separated) so cross-chain transfers whose legs are on different
    // wallets (e.g. Solana source + Tron dest) are matched — a single address would miss them.
    const joined = Array.isArray(address) ? address.join(',') : address
    return fetchApi(`/bridge/history/${joined}`)
  },

  /**
   * Check if a transfer exists in our database
   */
  async checkTransferExists(burnTxHash: string): Promise<boolean> {
    const response = await fetchApi<Transfer>(`/bridge/hybrid/status/${burnTxHash}`)
    return response.success && !!response.data
  },
}

/**
 * Wallet balance API endpoints
 */
export const balanceApi = {
  /**
   * Get USDC and native balance for an address on a specific chain
   * Backend endpoint: GET /api/wallet/balances?address=...&chain=...
   */
  async getBalance(
    address: string,
    chain: Chain
  ): Promise<ApiResponse<{ native: string; usdc?: string; usdt?: string; usdg?: string }>> {
    const response = await fetchApi<{
      chain: string
      address: string
      usdcBalance?: string
      usdtBalance?: string
      usdgBalance?: string
      nativeBalance: string
      nativeCurrency: string
      explorerUrl: string
    }>(`/wallet/balances?address=${encodeURIComponent(address)}&chain=${chain}`)

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || { code: 'UNKNOWN_ERROR', message: 'Failed to fetch balance' },
      }
    }

    // Map backend response to frontend format. usdc is now optional: Robinhood (#174) has no USDC —
    // its dollar is USDG. Only surface a stable the chain actually returns.
    return {
      success: true,
      data: {
        native: response.data.nativeBalance,
        ...(response.data.usdcBalance && { usdc: response.data.usdcBalance }),
        ...(response.data.usdtBalance && { usdt: response.data.usdtBalance }),
        ...(response.data.usdgBalance && { usdg: response.data.usdgBalance }),
      },
    }
  },

  /**
   * Get balances across all supported chains for an address
   * Backend endpoint: GET /api/wallet/balances?address=...&allChains=true
   */
  async getAllBalances(
    address: string
  ): Promise<ApiResponse<Partial<Record<Chain, { native: string; usdc?: string; usdt?: string; usdg?: string }>>>> {
    const response = await fetchApi<
      Record<string, { usdcBalance?: string; usdtBalance?: string; usdgBalance?: string; nativeBalance: string; error?: string }>
    >(`/wallet/balances?address=${encodeURIComponent(address)}&allChains=true`)

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || { code: 'UNKNOWN_ERROR', message: 'Failed to fetch balances' },
      }
    }

    // Map backend response to frontend format
    // Include both bridgeable and dexRoutable chains (e.g. BSC)
    const BALANCE_CHAINS = [...new Set([...BRIDGE_CHAINS, ...DEX_CHAINS])].filter(c => c !== 'tron')
    const balances: Partial<Record<Chain, { native: string; usdc?: string; usdt?: string; usdg?: string }>> = {}

    for (const chain of BALANCE_CHAINS) {
      const entry = response.data[chain]
      if (entry && !entry.error) {
        balances[chain] = {
          native: entry.nativeBalance,
          ...(entry.usdcBalance && { usdc: entry.usdcBalance }),
          ...(entry.usdtBalance && { usdt: entry.usdtBalance }),
          ...(entry.usdgBalance && { usdg: entry.usdgBalance }),
        }
      }
    }

    return {
      success: true,
      data: balances,
    }
  },
}

/**
 * Config API endpoints
 */
export const configApi = {
  /**
   * Get supported chains configuration
   */
  async getSupportedChains(): Promise<ApiResponse<Chain[]>> {
    return fetchApi('/config/chains')
  },

  /**
   * Get current fee configuration
   */
  async getFeeConfig(): Promise<
    ApiResponse<{ baseFeePercent: number }>
  > {
    return fetchApi('/config/fees')
  },
}

/**
 * Wallet tracking API endpoints
 * Used for recovery optimization and analytics
 */
export const walletApi = {
  // #478-W — `checkWallet()` is DELETED, not deprecated.
  //
  // It wrapped GET /api/wallet/check, whose sole caller was `useRecovery.ts`'s existence gate.
  // Section B deleted that gate (a `wallets` row is no longer created by POST /wallet/track, so
  // "no row" stopped meaning "nothing to recover"), which left this method with zero callers;
  // section D then moved the route behind `requireSecretKey()`, which makes it UNREACHABLE from a
  // browser at all — this bundle only ever carries the public pk_ key, and `requireSecretKey()`
  // prefix-rejects a `pk_` key with 403 before any lookup (`api-auth.ts:152-157`), never 401.
  // Leaving the method would have left a helper that compiles, looks usable, and 403s on every
  // call.
  //
  // Nothing else in `apps/web` referenced `/wallet/check`; the route survives for ops.

  /**
   * Track a wallet's transfer attempt
   * Called immediately after user approves the transaction
   */
  async trackTransferAttempt(params: {
    address: string
    chainType: string
    chainUsed: string
    amount: string
    destinationAddress: string
  }): Promise<ApiResponse<{ tracked: boolean }>> {
    return fetchApi('/wallet/track', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  /**
   * Track a successful transfer completion
   * Called when mint_success status is received
   */
  async trackTransferSuccess(params: {
    address: string
    amount: string
    destinationAddress: string
  }): Promise<ApiResponse<void>> {
    return fetchApi('/wallet/track/success', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  /**
   * Check block explorers for on-chain burns not in our database
   * Used as fallback when localStorage is wiped
   */
  async checkBurns(
    address: string,
    chain?: 'ethereum' | 'base' | 'solana'
  ): Promise<ApiResponse<{
    burns: OnChainBurn[]
    totalFound: number
    alreadyKnown: number
  }>> {
    const params = new URLSearchParams({ address })
    if (chain) params.set('chain', chain)
    return fetchApi(`/wallet/check-burns?${params}`)
  },
}

/**
 * On-chain burn found by block explorer
 */
export interface OnChainBurn {
  txHash: string
  chain: 'ethereum' | 'base' | 'solana'
  from: string
  timestamp: number
  blockNumber?: number
}

/**
 * Transfer intent status
 */
// ── DEX API Types ───────────────────────────────────────────────────

export interface DexEstimateResponse {
  corridor: string
  provider: string
  estimatedAllInBps: number
  breakdown: {
    protocolBps: number
    takerBps: number
    opexBps: number
    /** NOMINAL: the affiliate bps we asked the provider for. NOT what it applied — see below. */
    affiliateBps: number
    bufferBps: number
    /**
     * #261 F5 F1 — the THIRD-PARTY rail cost alone (protocol + taker + opex + buffer), i.e.
     * `estimatedAllInBps` minus its affiliate component. FRACTIONAL — a realistic corridor is 4.2 —
     * so it is published to 2 dp and must never be truncated to an integer by a consumer.
     *
     * Published by the deBridge same-chain estimate branch only (`dex.ts:1179`); it is not
     * affiliate-derived and carries no net/gross meaning. `undefined` does NOT mean only "an older
     * backend": it is also absent, on a CURRENT backend, from every response shape that never sets it —
     * Jupiter (`dex.ts:832`, `:873`), near_1click (`:1057`), and the no-cache-row fallback (`:1124`),
     * none of which publish a `breakdown.thirdPartyBps` at all. Either way — old backend or a
     * non-deBridge-corridor rail — the netted path cannot engage there, so the FE's non-netted
     * `allInBps × receive` fallback is correct regardless of which reason applies. Consumers must also
     * treat a NEGATIVE value as absent (BridgePage.tsx's `additiveTotalOk` requires `>= 0`): this field
     * derives from `corridor_fee_cache`, written from provider cost lines that `fee-oracle.ts:666-684`
     * does not sign-validate, so a malformed provider response can make it negative. Never reconstruct
     * this by subtracting `affiliateFeeBpsRealized` from `estimatedAllInBps` — those two are quoted at
     * different rates (nominal vs realized), so the difference is not the rail cost and goes negative in
     * the mid band.
     */
    thirdPartyBps?: number
    /**
     * #261 F5 — MEASURED. The affiliate rate deBridge actually applied, read off its own
     * `SingleChainSwapAffiliateFee` cost line (`payload.feeBps`). Differs from `affiliateBps`: a
     * nominal 1300 is clamped to a realized 999 by the provider's 10% ceiling. Same-chain deBridge
     * only; absent on every other route, and absent from an older backend.
     */
    affiliateFeeBpsRealized?: number
  }
  /**
   * Real prepended operating-expense (give − input) in USD for cross-chain; ~0 same-chain.
   * NULL = UNKNOWN, never zero: on a cross-chain corridor with no live quote the backend withholds
   * this rather than publishing the ~2e-11 cached value, which would silently disarm the opex gate.
   * Every consumer must treat null as "unpriced" and defer to quoteUnavailable / viability.
   */
  opexUsd: number | null
  /** Route floor: minimum recommended input (opex floor × buffer, rounded up to $1). 0 same-chain. */
  minAmountUsd?: number
  /**
   * #334 bug-3 — true when `minAmountUsd` rests on a real measurement (the live output wedge, or the
   * cached deBridge fixFee when the live quote failed), false/absent when it is only the ~0 cache-opex
   * fallback. The FE names the minimum in the CTA ONLY when this is set — otherwise a garbage ~$1 min on
   * a ~$25 route would show. Replaces the old `opexUsd != null` proxy, which never fired on a failed
   * cross-chain quote, so the min was never named (the bare "Amount too low for this route" bug).
   */
  minAmountTrusted?: boolean
  /**
   * HARD below-minimum marker (1Click 400): the backend refused to quote because input < the route floor.
   * Set CLIENT-side from the 400 body — the quote fields (estimatedReceived, opexUsd, breakdown…) are ABSENT
   * on a marker, so every consumer MUST gate on this before reading them. deBridge's below-min is a normal
   * 200 with a large opexUsd instead, so this is unset there.
   */
  belowMinimum?: boolean
  /**
   * A cross-chain route the provider could NOT price (verified in prod: →Tron under ~$25 returns
   * deBridge HTTP 400 errorCode 12 ERROR_LOW_GIVE_AMOUNT). When true the quote is UNKNOWN — the CTA
   * must be gated. Always accompanied by viability.level === 'block' and estimatedReceived === null.
   */
  quoteUnavailable?: boolean
  /** Why the route couldn't be priced: 'amount_below_route_cost' (input < flat route cost) | 'provider_error'. */
  quoteUnavailableReason?: string
  /**
   * #131 — the corridor is refused by POLICY, not by pricing: it is not a route we will ever serve at
   * any amount (today: Tron USDC cross-chain, which no venue can fill). Carries NO quote fields, so
   * consumers MUST gate on it before reading them. Always accompanied by viability.level === 'block',
   * so a consumer that never learns about this marker still hard-disables the CTA.
   */
  routeUnsupported?: boolean
  /** Machine-readable policy code, e.g. 'TRON_USDC_SAME_CHAIN_ONLY'. */
  routeUnsupportedCode?: string
  /** User-facing explanation naming the SUPPORTED path (rendered verbatim — never recomposed). */
  routeUnsupportedReason?: string
  /**
   * Economic-viability gate. 'block' = nothing meaningful arrives (receive ≤ 0, the ALL-IN cost eats
   * ≥100% of the input, the route could not be priced at all, or it is policy-refused); 'warn' = more
   * than VIABILITY_WARN_RATIO (25%, #131) of the input lost to fees.
   *
   * #131 — `wedgeUsd` is the ALL-IN cost (input + fixFee − receive), NOT the output wedge: on a
   * stablecoin-source route deBridge's flat fixFee is a separate native payment the wedge cannot see.
   * `ratio` is that cost ÷ input, computed SERVER-side — render it, never recompute it here.
   * neededUsd is the advisory "send at least ~$X" hint (0 when unpriceable).
   */
  viability?: { level: 'ok' | 'warn' | 'block'; neededUsd: number; wedgeUsd: number; ratio?: number }
  /** Null when the route could not be priced — NEVER render a fabricated receive in its place. */
  estimatedReceived: string | null
  estimatedTokenAmount?: string
  minTokenAmount?: string
  destDecimals?: number
  /**
   * #261 F5 — `estimatedTokenAmount` / `minTokenAmount` above are NET of the Parse affiliate skim.
   * Set from the PRESENCE of deBridge's own `SingleChainSwapAffiliateFee` cost line on a same-chain
   * quote, so it is the provider's report that it took the fee — never an inference from `provider`
   * or from the request we sent. Absent ⇒ the receive is gross and the client's pre-F5 fee formulas
   * apply unchanged (which is also what an older backend produces).
   */
  receiveIsNetOfAffiliate?: boolean
  /**
   * #261 F5 — the exact skim that came out of the receive, in DEST-token display units, from that
   * same cost line's `payload.feeAmount`. Travels with `receiveIsNetOfAffiliate` or not at all: the
   * flag without the amount would leave a gross formula running against a net receive. Never derive
   * a substitute when it is absent — its absence means no affiliate fee was charged.
   */
  affiliateFeeAmount?: string
  /** USD value of the input amount (for the "You send" fiat subtitle; native source ≠ $1). */
  sourceAmountUsd?: number | null
  /** deBridge flat protocol fee in the SOURCE CHAIN's native token (e.g. "0.001"). Paid on top of input. */
  fixFeeNative?: string
  /** Symbol of that native token (ETH/POL/BNB…) — for stable source it differs from the source token. */
  fixFeeSymbol?: string
  fixFeeUsd?: number
  /** Total source debit the wallet will quote (input + opex + fixFee), in source-token units, excl. gas. */
  youPaySource?: string
  youPayUsd?: number
  /** All-in fee in USD = youPayUsd − receiveUsd (captures fixed fee, route margin, price impact, affiliate). */
  feesTotalUsd?: number
  /** Generic provider/rail fee in USD (e.g. the 1Click rail). Itemized separately from the Parse fee so
   *  Parse fee + Network fee = Total. Not a deBridge fixFee (that's fixFeeNative). */
  networkFeeUsd?: number
  /** Our actual Parse/affiliate fee in USD (source-sized). For near_1click this is the 1Click appFee, which
   *  is deducted from the SOURCE token — so it's correct where (affiliateBps × destAmount) would understate. */
  parseFeeUsd?: number
  /** Tron source-side energy burn in USD (TRX spent to energize the TRC-20 deposit transfer). Present only
   *  for a Tron-source 1Click swap. tronHasEnergy = the sender already has enough staked/rented energy. */
  tronEnergyFeeUsd?: number
  tronHasEnergy?: boolean
  /** Estimated source-chain gas reserve in native units (swap gasLimit × gas price) — for the native-balance pre-check. */
  gasEstimateNative?: string
  cacheAge: number
  feeWarningTier: number | null
}

// SupportedToken and FALLBACK_TOKENS re-exported from @/config/fallback-tokens above

export interface RouteConfig {
  sourceChain: string
  destChain: string
  sourceToken: string
  destToken: string
  provider: string
  affiliateFeeBps: number
}

export interface DexBridgeRequest {
  sourceChain: string
  destChain: string
  sourceToken: string
  destToken: string
  amount: string
  recipientAddress: string
  senderAddress: string
  /** Advisory native balance (wei string) for the on-click affordability check. Native source only. */
  senderNativeBalance?: string
  /** Advisory source-token balance (atomic) for the cross-chain ERC-20 opex/viability gates. */
  senderTokenBalance?: string
  /**
   * Referral code from localStorage ('parsebridge:ref'). Forwarded ONLY on the swap SUBMIT (this
   * build/execute call), never on estimate/reverse-estimate — a first-time referee whose FIRST action
   * is a swap has no persisted wallet attribution yet, so the backend uses this as a fallback to
   * attribute (and pay, via the on-chain split) the referrer. Backend writes it only on execution.
   */
  ref?: string
  /**
   * #614 Phase A — beta-first preview opt-in. Set by `submitBridge` itself (never by callers) when the
   * beta-only flag is on and the pair is tron→tron; the API honours it behind its own SSM switch by
   * treating a still-deBridge TRX↔USDT row as `near_1click` for THIS request. The only accepted value.
   */
  routingPreview?: 'tron_samechain_1click'
}

export interface DexBridgeResponse {
  transferId: string
  orderId?: string
  tx: Record<string, unknown>
  /**
   * deBridge's own gas hints for the swap tx (sibling of `tx`, not inside it). Present on
   * same-chain swaps; the send path applies `gasLimit` so heavy-calldata aggregators don't trip a
   * wallet gas under-estimate. Absent on cross-chain → wallet estimates as before.
   */
  gas?: { gasLimit?: string; maxFeePerGas?: string; maxPriorityFeePerGas?: string }
  approxFillDelay?: number
  /**
   * Exact atomic give the router will transferFrom — the bounded ERC-20/TRC-20 approval amount
   * (input for same-chain; input + operating expense for cross-chain). Null/absent for native
   * source (no approval). The wallet approves exactly this instead of MAX_UINT256.
   */
  approvalAmount?: string | null
  /**
   * B3: set when a first-time cross-chain ERC-20 source needs a one-time approval before deBridge
   * will build the swap tx. The client approves `approvalAmount` (or `allowanceValue`) to
   * `allowanceTarget` (the DLN router), then re-submits to receive the real `tx`.
   */
  needsApproval?: boolean
  allowanceTarget?: string
  allowanceValue?: string
  provider: string
  quote?: {
    outAmount: string
    outDecimals?: number
    inDecimals?: number
    priceImpactPct?: string
    platformFee?: { amount?: string; feeBps?: number }
    routePlan?: Array<{ swapInfo?: { label?: string }; percent?: number }>
  }
  // NEAR 1Click (Tron corridor, Phase A): deposit-address settlement. The user TRANSFERS the input
  // to `depositAddress` (no calldata/approval); these fields drive the FE quote display + send.
  depositAddress?: string | null
  depositMemo?: string | null
  amountOut?: string
  minAmountOut?: string
  deadline?: string | null
  timeEstimate?: number | null
  withdrawFee?: string | null
}

export interface DexOrderStatus {
  orderId?: string
  state?: string
  fulfilledDstEventMetadata?: { transactionHash?: string }
  createdSrcEventMetadata?: { transactionHash?: string }
  taker?: string
}

/** #614 Phase A — the single accepted `routingPreview` value (the API rejects anything else with a 400). */
export const TRON_SAMECHAIN_PREVIEW = 'tron_samechain_1click' as const

/**
 * #614 Phase A — does this request carry the beta preview opt-in? True only when the beta-only flag is
 * on AND both chains are tron. The API decides the token pair; sending the opt-in on a pair it does not
 * cover is a no-op there. Exported so the services test can pin the predicate directly.
 */
export function tronSamechainPreviewOptIn(sourceChain: string | null | undefined, destChain: string | null | undefined): boolean {
  return isTronSamechainOneclickPreviewEnabled() && sourceChain === 'tron' && destChain === 'tron'
}

/**
 * DEX API endpoints
 */
export const dexApi = {
  async getEstimate(params: {
    sourceChain: string
    destChain: string
    sourceToken: string
    destToken: string
    amount: string
    /** Source wallet — only used for Tron source, to read the user's energy for an accurate burn cost. */
    senderAddress?: string
  }): Promise<ApiResponse<DexEstimateResponse> & { minAmountUsd?: number }> {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) as Record<string, string>,
    )
    /**
     * #261 F5 / G-C2 — the stale-tab marker, set HERE rather than threaded through callers.
     *
     * It says "this bundle knows how to display a receive that is net of the Parse affiliate fee".
     * The backend only nets the same-chain quote when it sees this, so a tab still running the OLD
     * bundle against the NEW backend keeps getting the gross receive its formulas expect, instead of
     * silently under-reporting the fee for the life of that tab. A cached SPA with no service worker
     * has no other way to make a deploy atomic for the client.
     *
     * Inside the helper deliberately: it then rides EVERY estimate call — this page's quote query and
     * `useDexBridge`'s pre-submit estimate alike — with no plumbing and no second place to forget it.
     */
    query.set('netOfAffiliate', '1')
    // #614 Phase A — the beta preview opt-in rides every tron→tron estimate when the beta-only flag is
    // on. Set HERE for the same reason as the marker above: one place, no caller can forget it.
    if (tronSamechainPreviewOptIn(params.sourceChain, params.destChain)) query.set('routingPreview', TRON_SAMECHAIN_PREVIEW)
    // fetchApi preserves a top-level `minAmountUsd` on a 400 body (input below the 1Click route floor); widen
    // the return type so the caller can distinguish a HARD below-minimum from other estimate failures (which
    // carry no minAmountUsd).
    return fetchApi(`/dex/estimate?${query}`) as Promise<ApiResponse<DexEstimateResponse> & { minAmountUsd?: number }>
  },

  // Reverse quote: given a desired dest amount, returns the required source amount
  // (deBridge exact-out). Same-chain swaps only.
  async getReverseEstimate(params: {
    sourceChain: string
    destChain: string
    sourceToken: string
    destToken: string
    destAmount: string
  }): Promise<ApiResponse<{ sourceAmount: string; sourceToken: string; sourceDecimals: number }>> {
    const query = new URLSearchParams(params)
    if (tronSamechainPreviewOptIn(params.sourceChain, params.destChain)) query.set('routingPreview', TRON_SAMECHAIN_PREVIEW)
    return fetchApi(`/dex/reverse-estimate?${query}`)
  },

  async getTokens(): Promise<ApiResponse<SupportedToken[]>> {
    return fetchApi('/dex/tokens')
  },

  async getRoutes(): Promise<ApiResponse<RouteConfig[]>> {
    // #614 Phase A (reviewer B1) — the routes list is what every PRE-SUBMIT seam keys on
    // (selectedRoute.provider), so the beta opt-in rides it too; the API overlays only the two Tron
    // same-chain cells and leaves every other row untouched. Flag off → a byte-identical request.
    return fetchApi(isTronSamechainOneclickPreviewEnabled() ? `/dex/routes?routingPreview=${TRON_SAMECHAIN_PREVIEW}` : '/dex/routes')
  },

  async submitBridge(request: DexBridgeRequest): Promise<ApiResponse<DexBridgeResponse>> {
    const body: DexBridgeRequest = tronSamechainPreviewOptIn(request.sourceChain, request.destChain)
      ? { ...request, routingPreview: TRON_SAMECHAIN_PREVIEW }
      : request
    return fetchApi('/dex/bridge', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async confirmBridge(transferId: string, sourceTxHash: string): Promise<ApiResponse<{
    transferId: string
    sourceTxHash: string
    alreadyConfirmed: boolean
  }>> {
    return fetchApi('/dex/confirm', {
      method: 'POST',
      body: JSON.stringify({ transferId, sourceTxHash }),
    })
  },

  // Batch B: /cancel now REQUIRES a wallet signature owning the order (the shared public api key is NOT
  // proof of ownership). Build `auth` with the cancelAuth helpers (sign the ParseCancel payload). A 403
  // response means the authorization failed/expired — the caller should surface a "try again" state.
  async cancelOrder(
    transferId: string,
    auth: { signature: string; wallet: string; nonce: string; deadline: number },
  ): Promise<ApiResponse<{ success: boolean }>> {
    return fetchApi('/dex/cancel', {
      method: 'POST',
      body: JSON.stringify({ transferId, ...auth }),
    })
  },

  async getOrderStatus(transferId: string): Promise<ApiResponse<DexOrderStatus>> {
    return fetchApi(`/dex/order/${encodeURIComponent(transferId)}`)
  },
}

export type IntentStatus = 'pending' | 'cancelled' | 'burned' | 'submitted' | 'completed' | 'expired'

/**
 * Transfer intent record from the backend
 */
export interface TransferIntent {
  id: string
  source_address: string
  destination_address: string
  source_chain: string
  destination_chain: string
  amount: string
  usdc_balance_at_intent: string | null
  created_at: number
  updated_at: number
  status: IntentStatus
  burn_tx_hash: string | null
  expires_at: number
}

/**
 * Intent API endpoints
 * Used for pre-burn intent tracking to ensure zero orphaned burns
 *
 * Flow:
 * 1. trackIntent() - Called BEFORE wallet approval (when user clicks Send)
 * 2. updateIntent() - Called on cancel/burn confirm/completion
 * 3. checkIntents() - Called during recovery to find pending intents
 */
export const intentApi = {
  /**
   * Create a transfer intent before wallet approval
   * Called when user clicks "Send" button, BEFORE the wallet popup
   *
   * If DB_INTENT_REQUIRED=true (default):
   * - Success: Return intent ID, proceed with wallet popup
   * - Failure: Block user, show error, they must retry
   *
   * If DB_INTENT_REQUIRED=false:
   * - Success: Return intent ID
   * - Failure: Continue with localStorage fallback
   */
  async trackIntent(params: {
    sourceAddress: string
    destinationAddress: string
    sourceChain: Chain
    destinationChain: Chain
    amount: string
    usdcBalanceAtIntent?: string
  }): Promise<ApiResponse<{
    intentId: string
    intent: TransferIntent
    dbIntentRequired: boolean
  }>> {
    return fetchApi('/intent/track', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  /**
   * Update a transfer intent
   * Called on wallet cancel, burn confirm, backend submission, or mint success
   */
  async updateIntent(
    intentId: string,
    status: IntentStatus,
    burnTxHash?: string
  ): Promise<ApiResponse<{ intent: TransferIntent }>> {
    return fetchApi(`/intent/${intentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, burnTxHash }),
    })
  },

  /**
   * Check for pending/burned intents for a wallet address
   * Used during recovery flow to find incomplete transfers
   *
   * Returns:
   * - intents: All pending/burned intents
   * - staleIntents: Pending intents older than staleMinutes (may need explorer check)
   * - needsExplorerCheck: Boolean flag if stale intents exist
   */
  async checkIntents(
    address: string,
    staleMinutes = 10
  ): Promise<ApiResponse<{
    intents: TransferIntent[]
    staleIntents: TransferIntent[]
    hasStaleIntents: boolean
    needsExplorerCheck: boolean
  }>> {
    const params = new URLSearchParams({
      address,
      staleMinutes: String(staleMinutes),
    })
    return fetchApi(`/intent/check?${params}`)
  },

  /**
   * Get a specific intent by ID
   */
  async getIntent(intentId: string): Promise<ApiResponse<{ intent: TransferIntent }>> {
    return fetchApi(`/intent/${intentId}`)
  },

  /**
   * Match an on-chain burn to an intent
   * Used during recovery when block explorer finds a burn
   */
  async matchBurnToIntent(params: {
    sourceAddress: string
    amount: string
    destinationChain: Chain
    burnTimestamp: number
  }): Promise<ApiResponse<{
    matched: boolean
    intent: TransferIntent | null
  }>> {
    return fetchApi('/intent/match', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },
}

/**
 * Referral API endpoints
 */
export const referralApi = {
  async register(data: {
    evmAddress: string
    solAddress?: string
    signature: string
  }): Promise<ApiResponse<ReferrerRegistration>> {
    return fetchApi('/referrer/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async renew(data: {
    referralCode: string
    newExpiry: string
    signature: string
  }): Promise<ApiResponse<{ referralCode: string; expiresAt: number; renewalCount: number }>> {
    return fetchApi('/referrer/renew', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getStats(code: string): Promise<ApiResponse<ReferrerStats>> {
    return fetchApi(`/referrer/${encodeURIComponent(code)}/stats`)
  },

  // Server-side check of whether a referrer's Solana USDC associated-token-account exists.
  // Performed on the backend so the Helius RPC key never ships in the browser (EW-2b).
  // `available: false` means the backend RPC couldn't be reached — treat as "couldn't check"
  // (render "—"), NOT as "missing".
  async getSolUsdcStatus(solAddress: string): Promise<ApiResponse<{
    available: boolean
    exists: boolean | null
    missing: boolean | null
  }>> {
    return fetchApi(`/referrer/sol-usdc-balance?address=${encodeURIComponent(solAddress)}`)
  },

  async getEarnings(referralCode: string): Promise<ApiResponse<ReferrerEarnings>> {
    return fetchApi(`/referrer/${encodeURIComponent(referralCode)}/earnings`)
  },

  async recordClaim(referralCode: string, data: {
    chain: string
    chainId: number
    token: string
    tokenSymbol: string
    amount: string
    txHash: string
  }): Promise<ApiResponse<{ recorded: boolean }>> {
    return fetchApi(`/referrer/${encodeURIComponent(referralCode)}/claims`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getClaimsByWallet(address: string): Promise<ApiResponse<Array<{
    chain: string
    chainId: number
    tokenSymbol: string
    amount: string
    txHash: string
    claimedAt: string
  }>>> {
    return fetchApi(`/referrer/claims/wallet/${encodeURIComponent(address)}`)
  },

  async walletConnect(data: {
    address: string
    ref?: string
  }): Promise<ApiResponse<{ connected: boolean; referred: boolean }>> {
    return fetchApi('/wallet/connect', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ── Prices API (P5g — fiat conversion rates) ─────────────────────────
export const pricesApi = {
  async getUsdcRates(): Promise<{
    rates: Record<string, number>
    stale: boolean
    updatedAt: number
  } | null> {
    const res = await fetchApi<{
      rates: Record<string, number>
      stale: boolean
      updatedAt: number
    }>('/prices/usdc')
    if (!res.success || !res.data) return null
    return res.data
  },
}
